import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import AdsDemo from "@/components/services/AdsDemo";
import MetaAdsDemo from "@/components/services/MetaAdsDemo";
import TikTokAdsDemo from "@/components/services/TikTokAdsDemo";
import SocialDemo from "@/components/services/SocialDemo";
import GoogleAdsDemo from "@/components/services/GoogleAdsDemo";
import ChatGPTAdsDemo from "@/components/services/ChatGPTAdsDemo";
import SEODemo from "@/components/services/SEODemo";
import SEOLocalDemo from "@/components/services/SEOLocalDemo";
import CRMDemo from "@/components/services/CRMDemo";
import AutomationDemo from "@/components/services/AutomationDemo";
import ConsultoriaPuntualDemo from "@/components/services/ConsultoriaPuntualDemo";
import WebsitesDemo from "@/components/services/WebsitesDemo";
import WebCorporativaDemo from "@/components/services/WebCorporativaDemo";
import TiendaOnlineDemo from "@/components/services/TiendaOnlineDemo";
import CRMImplementacionDemo from "@/components/services/CRMImplementacionDemo";
import BrandingDemo from "@/components/services/BrandingDemo";
import LandingPageDemo from "@/components/services/LandingPageDemo";
import EmailMarketingDemo from "@/components/services/EmailMarketingDemo";
import MantenimientoWebDemo from "@/components/services/MantenimientoWebDemo";
import IncubadoraDemo from "@/components/services/IncubadoraDemo";
import ServiceIncludes from "@/components/services/ServiceIncludes";
import ServiceQuickInfo, { QuickInfoData } from "@/components/services/ServiceQuickInfo";
import NotFound from "./NotFound";
import { SERVICES_SEO, SITE, breadcrumbForService, serviceSchema, serviceUrl } from "@/lib/seo";
import { SERVICE_GUIDE, RELATED_SERVICES, SERVICE_LABELS } from "@/data/service-links";
import type { ServiceSlug } from "@/data/service-slugs";

interface IncludesGroup {
  title?: string;
  items: string[];
}

interface ServiceConfig {
  title: string;
  description: string;
  component: React.ComponentType<any>;
  hideWrapper?: boolean;
  hideCTA?: boolean;
  includes?: IncludesGroup[];
  includesIntro?: { title?: string; description?: string };
  quickInfo?: QuickInfoData;
}

const serviceConfig: Record<string, ServiceConfig> = {
  "publicidad-meta": {
    title: "Publicidad en Meta",
    description: "Gestión de campañas publicitarias en Facebook e Instagram Ads.",
    component: MetaAdsDemo,
    hideWrapper: true,
    hideCTA: true,
  },
  "publicidad-tiktok": {
    title: "Publicidad en TikTok",
    description: "Gestión de campañas publicitarias en TikTok Ads.",
    component: TikTokAdsDemo,
    hideWrapper: true,
    hideCTA: true,
  },
  "redes-sociales": {
    title: "Gestión de Redes Sociales",
    description: "Gestión orgánica de redes sociales con producción de contenido y publicación multicanal.",
    component: SocialDemo,
    hideWrapper: true,
    hideCTA: true,
    quickInfo: {
      deliveryTime: "Setup en los primeros 14 días. Entregables recurrentes cada mes.",
      included:
        "8 piezas/mes (3 estáticos + 3 carruseles + 2 vídeos verticales), calendario editorial y publicación multicanal con Metricool",
      notIncluded:
        "community management (lo hace el cliente), producción de vídeo profesional, sesiones fotográficas y gestión de crisis",
      deliverable:
        "Calendario editorial aprobado, 8 piezas publicadas en todas las redes y reporte mensual el día del ciclo de facturación.",
    },
    includes: [
      {
        title: "Setup (primeros 14 días)",
        items: [
          "Estrategia de contenido (ejes, tono, frecuencia)",
          "Definición de identidad visual",
          "Primer calendario editorial",
        ],
      },
      {
        title: "Recurrente (cada mes)",
        items: [
          "8 piezas/mes: 3 estáticos + 3 carruseles + 2 vídeos verticales",
          "Calendario editorial entregado para aprobación del cliente",
          "Adaptación al formato de cada red social",
          "Publicación en todas las redes activas (Instagram, Facebook, TikTok, LinkedIn, YouTube Shorts)",
          "Programación con Metricool",
          "Reporte el día del ciclo de facturación",
        ],
      },
    ],
  },
  "google-ads": {
    title: "Campañas de Google Ads",
    description: "Gestión de campañas en Google Ads (Search, Performance Max, Display).",
    component: GoogleAdsDemo,
    hideWrapper: true,
    hideCTA: true,
  },
  "chatgpt-ads": {
    title: "Anuncios en ChatGPT Ads",
    description: "Gestión de campañas publicitarias en ChatGPT, el nuevo canal de anuncios de OpenAI.",
    component: ChatGPTAdsDemo,
    hideWrapper: true,
    hideCTA: true,
  },
  seo: {
    title: "SEO",
    description: "Posicionamiento orgánico en buscadores para negocios con presencia online.",
    component: SEODemo,
    hideWrapper: true,
    hideCTA: true,
  },
  "seo-local": {
    title: "SEO Local",
    description: "Posicionamiento local para negocios físicos con clientela geográfica.",
    component: SEOLocalDemo,
    hideWrapper: true,
    hideCTA: true,
  },
  consultoria: {
    title: "Consultoría Estratégica",
    description: "Servicio puntual de consultoría: 2 sesiones con un experto y un PDF con análisis y plan de acción. Pago único de 320€.",
    component: ConsultoriaPuntualDemo,
    hideWrapper: true,
    hideCTA: true,
  },
  "web-corporativa": {
    title: "Web Corporativa",
    description: "Web corporativa profesional de hasta 8 páginas, multilingüe, con hosting y dominio incluidos el primer año. Pago único de 1.500€.",
    component: WebCorporativaDemo,
    hideWrapper: true,
    hideCTA: true,
  },
  "tienda-online": {
    title: "Tienda Online",
    description: "Tienda online completa con productos ilimitados, redacción de fichas, multilingüe y pasarela de pago. Pago único de 3.000€.",
    component: TiendaOnlineDemo,
    hideWrapper: true,
    hideCTA: true,
  },
  "crm-implementacion": {
    title: "Embudo de Ventas",
    description: "Embudo completo con Kommo: setup, pipeline y automatizaciones. Incluye 7 meses de licencia con 1 usuario. Pago único de 900€.",
    component: CRMImplementacionDemo,
    hideWrapper: true,
    hideCTA: true,
  },
  branding: {
    title: "Branding e Identidad de Marca",
    description: "Logo, variantes, paleta, tipografías y brandbook completo. Pago único de 500€.",
    component: BrandingDemo,
    hideWrapper: true,
    hideCTA: true,
  },
  "landing-page": {
    title: "Landing Page",
    description: "Landing one-page de conversión con textos, formulario y tracking integrado. Pago único de 490€.",
    component: LandingPageDemo,
    hideWrapper: true,
    hideCTA: true,
  },
  "email-marketing": {
    title: "Email Marketing",
    description: "Correos diseñados para vender y fidelizar: hasta 4 correos al mes, plantillas y automatizaciones básicas con Brevo.",
    component: EmailMarketingDemo,
    hideWrapper: true,
    hideCTA: true,
  },
  automatizacion: {
    title: "Automatización",
    description: "Conectamos tus herramientas con Zapier: leads al CRM, recordatorios, seguimientos y facturación automáticos.",
    component: AutomationDemo,
    hideWrapper: true,
    hideCTA: true,
  },
  "mantenimiento-web": {
    title: "Mantenimiento Web",
    description: "Mantenimiento mensual de tu web: optimización de imágenes, velocidad de carga y recomendaciones de mejora. Webs propias o de terceros.",
    component: MantenimientoWebDemo,
    hideWrapper: true,
    hideCTA: true,
  },
  incubadora: {
    title: "Incubadora de Proyecto",
    description: "El producto más completo del catálogo: web, landing, embudo, reservas, automatizaciones, pagos y medición. Todo conectado en 90 días. Pago único de 5.900€ con 20% de descuento vitalicio en planes mensuales.",
    component: IncubadoraDemo,
    hideWrapper: true,
    hideCTA: true,
  },
  websites: {
    title: "Sitios Web & Landing Pages",
    description: "Diseño y mantenimiento de webs sencillas en suscripción + landing pages.",
    component: WebsitesDemo,
    hideWrapper: true,
    hideCTA: true,
    includes: [
      {
        items: [
          "Web sencilla en suscripción: hasta 5 páginas, plantilla base, entrega en 30 días",
          "Stack: WordPress + Elementor o Lovable",
          "Briefing inicial estructurado",
          "Setup técnico (hosting, dominio, SSL, plugins esenciales)",
          "Diseño adaptado a identidad visual del cliente",
          "Configuración SEO básica on-page",
          "Mantenimiento mensual: updates, plugins, seguridad, backups",
          "Monitorización Core Web Vitals y velocidad",
          "Hasta 2h/mes de modificaciones puntuales incluidas",
          "Conexión con Search Console, GA4 y herramientas",
        ],
      },
    ],
  },
};

const ServicioPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const config = slug ? serviceConfig[slug] : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!config) return <NotFound />;

  const ServiceComponent = config.component;
  const seo = slug ? SERVICES_SEO[slug] : undefined;
  const canonical = serviceUrl(slug!);
  const pageTitle = seo?.metaTitle ?? `${config.title} | Vibra Marketing`;
  const pageDescription = seo?.metaDescription ?? config.description;
  const ogImage = SITE.ogImage;
  const schemas = [seo && serviceSchema(slug!), seo && breadcrumbForService(slug!)].filter(Boolean);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang="es" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {seo?.keywords?.length ? <meta name="keywords" content={seo.keywords.join(", ")} /> : null}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="es-ES" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Vibra Marketing" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />
        {schemas.map((s, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
        ))}
      </Helmet>
      <Header />
      <main className="pt-24 pb-16">
        <div className="container px-6">
          {/* Breadcrumb visible */}
          <nav aria-label="Migas de pan" className="mb-6 text-sm text-muted-foreground">
            <ol className="flex flex-wrap items-center gap-2">
              <li><Link to="/" className="hover:text-foreground">Inicio</Link></li>
              <li aria-hidden>/</li>
              <li><Link to="/#services" className="hover:text-foreground">Servicios</Link></li>
              <li aria-hidden>/</li>
              <li className="text-foreground font-medium">{config.title}</li>
            </ol>
          </nav>

          <Link to="/#services">
            <Button variant="ghost" className="mb-8 gap-2">
              <ArrowLeft size={16} />
              Volver a servicios
            </Button>
          </Link>

          {!config.hideWrapper && (
            <div className="max-w-2xl mb-12">
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">
                {config.title}
              </h1>
              <p className="text-lg text-muted-foreground">
                {config.description}
              </p>
            </div>
          )}

          <ServiceComponent platform={slug === "publicidad-tiktok" ? "tiktok" : "meta"} />

          {config.quickInfo && <ServiceQuickInfo data={config.quickInfo} />}

          {config.includes && config.includes.length > 0 && (
            <ServiceIncludes
              groups={config.includes}
              title={config.includesIntro?.title ?? "Exactamente lo que entregamos cada mes"}
              description={
                config.includesIntro?.description ??
                "Sin letra pequeña. Estos son los entregables concretos del servicio."
              }
            />
          )}

          {!config.hideCTA && (
            <div className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/20 text-center">
              <h3 className="text-2xl font-bold mb-3">¿Listo para empezar?</h3>
              <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                Consulta nuestros planes y elige el que mejor se adapte a tu negocio.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link to="/precios">
                  <Button size="lg" className="gap-2">
                    Ver precios
                    <ArrowRight size={16} />
                  </Button>
                </Link>
                {slug && SERVICE_GUIDE[slug as ServiceSlug] && (
                  <Link to={`/guias/${SERVICE_GUIDE[slug as ServiceSlug]}`}>
                    <Button size="lg" variant="outline" className="gap-2">
                      Leer la guía pilar
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Servicios relacionados */}
          {slug && RELATED_SERVICES[slug as ServiceSlug] && (
            <section className="mt-16">
              <h3 className="text-xl font-semibold mb-4 text-center">Servicios relacionados</h3>
              <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
                {RELATED_SERVICES[slug as ServiceSlug].map((rel) => (
                  <Link
                    key={rel}
                    to={`/servicios/${rel}`}
                    className="p-5 rounded-xl border border-border bg-card hover:border-primary/40 transition-colors text-center"
                  >
                    <div className="font-medium">{SERVICE_LABELS[rel]}</div>
                    <div className="text-xs text-muted-foreground mt-1">Ver servicio →</div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ServicioPage;
